import { toast } from "@/hooks/use-toast";
import { ApiErrorResponse } from "@/types/ApiResponse";
import { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { z, ZodString } from "zod";

const validateMultiLineEmailOrPhone = ({ input, match = "email" }: { input: ZodString; match: "email" | "phone" }) => {
	const config = (match => {
		const handles = {
			email: {
				fn: (emails: string[]) => emails.every(email => z.string().email().safeParse(email).success),
				message: {
					message: "One or more email addresses are invalid.",
				},
			},
			phone: {
				fn: (numbers: string[]) => numbers.every(num => /^\d{10}$/.test(num)),
				message: {
					message: "Each phone number must contain exactly 10 digits.",
				},
			},
		};
		return handles[match];
	})(match);

	return input.refine((val: string) => {
		const inputs = val
			.split(/\r?\n/)
			.map(line => line.trim())
			.filter(line => line !== "");
		return config.fn(inputs);
	}, config.message);
};

const showValidationErrors = <T extends FieldValues>({
	form,
	error,
	defaultMessage = "Invalid Form Data",
}: {
	form: UseFormReturn<T>;
	error: ApiErrorResponse;
	defaultMessage?: string;
}) => {
	const errors = error?.data?.errors ?? [];
	if (Object.keys(errors).length) {
		Object.entries(errors).forEach(([field, message]: [unknown, string]) =>
			form.setError(field as Path<T>, { message }),
		);
	}

	if (error?.message || error?.data?.message) {
		toast({
			variant: "destructive",
			description: error?.message ?? error?.data?.message ?? defaultMessage,
		});
	}
};

export { validateMultiLineEmailOrPhone, showValidationErrors };
